import { PatronQuestionType } from '../typings/AdditionalInformation'
import Cart from '../typings/Cart'
import Order from '../typings/Order'
import { PaymentState } from '../typings/Payment'

interface CheckoutStepsFlags {
  isBookableItemCart: boolean
  isProductItemCart: boolean
  hasProductToSelect: boolean
  hasSelectedProduct: boolean
  hasAdditionalInformation: boolean
  hasSomethingToPay: boolean
  hasBufferPage: boolean
  isOrderPaid: boolean
  isQuickCheckoutAvailable: boolean
}

const mapCheckoutDataToCheckoutStepsFlags = (data: Cart | Order): CheckoutStepsFlags => {
  const checkoutStepsFlags: CheckoutStepsFlags = {
    isBookableItemCart: false,
    isProductItemCart: false,
    hasProductToSelect: true,
    hasSelectedProduct: false,
    hasAdditionalInformation: false,
    hasSomethingToPay: true,
    isOrderPaid: false,
    hasBufferPage: false,
    isQuickCheckoutAvailable: false,
  }

  if (data.__typename === 'Cart') {
    const isBookableItemCart = data.items && data.items[0].__typename === 'BookableItemCartItem'
    const isProductItemCart = data.items && data.items[0].__typename === 'ProductCartItem'

    const hasProductToSelect =
      data.items &&
      ((data.items[0].purchaseOptions && data.items[0].purchaseOptions.length > 0) ||
        (data.items[0].confirmWithOptions && data.items[0].confirmWithOptions.length > 0))
    const hasSelectedProduct = data.items && Boolean(data.items[0].settleWith)

    const hasAddOns =
      data.items &&
      data.items[0].__typename === 'BookableItemCartItem' &&
      data.items[0].addOns &&
      data.items[0].addOns.length > 0

    const hasRequiredInformation = data.requiredInformation.length > 0
    const hasPolicies = data.policies.length > 0

    const hasAdditionalInformation =
      hasRequiredInformation ||
      hasAddOns ||
      hasPolicies ||
      data.patronQuestionType !== PatronQuestionType.PATRON_QUESTION_TYPE_NONE

    const hasTotalPriceToPay =
      isProductItemCart && data.priceTable && data.priceTable.total && data.priceTable.total.value > 0
    const hasOriginalPriceToPay =
      isProductItemCart && data.priceTable?.originalPrice && data.priceTable.originalPrice.value > 0

    const hasSomethingToPay = hasProductToSelect || hasTotalPriceToPay || hasOriginalPriceToPay

    // Currently used for when we want to wait for a user manual confirmation
    // that he really wants to go to the payment/conformation screen, currently
    // used for :
    // a) when buying a free product
    // b) when buying a product on a venue with voucher enabled and where no
    //    required information is necessary
    const hasBufferPage =
      isProductItemCart &&
      !hasProductToSelect &&
      !hasAdditionalInformation &&
      (!hasSomethingToPay || data.isVoucherEnabled)

    checkoutStepsFlags.isBookableItemCart = Boolean(isBookableItemCart)
    checkoutStepsFlags.isProductItemCart = Boolean(isProductItemCart)
    checkoutStepsFlags.hasProductToSelect = Boolean(hasProductToSelect)
    checkoutStepsFlags.hasSelectedProduct = Boolean(hasSelectedProduct)
    checkoutStepsFlags.hasAdditionalInformation = hasAdditionalInformation
    checkoutStepsFlags.hasBufferPage = Boolean(hasBufferPage)
    checkoutStepsFlags.hasSomethingToPay = Boolean(hasSomethingToPay)
    checkoutStepsFlags.isQuickCheckoutAvailable = Boolean(data.quickCheckoutAvailable)
  } else {
    const hasSomethingToPay = Boolean(data.priceTable && data.priceTable.total && data.priceTable.total.value > 0)
    const isOrderPaid = Boolean(data.payment && data.payment.state === PaymentState.PAYMENT_STATE_SUCCEEDED)

    checkoutStepsFlags.hasSomethingToPay = hasSomethingToPay
    checkoutStepsFlags.isOrderPaid = isOrderPaid
  }

  return checkoutStepsFlags
}

export default mapCheckoutDataToCheckoutStepsFlags
